import React, { useState, useEffect } from 'react';
import { apiClube } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Check, X, UserPlus, CalendarDays, Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const Mensalidades = () => {
  // Estados de Autenticação do Clube
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('club_token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Estados de Dados da Mensalidade
  const [members, setMembers] = useState([]);
  const [dues, setDues] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  // Função de Login Isolada da Tesouraria
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await apiClube.auth.login({ email, password });
      localStorage.setItem('club_token', response.data.token);
      setIsAuthenticated(true);
      toast.success("Acesso liberado ao Cofre!");
    } catch (error) {
      console.error(error);
      toast.error("Credenciais inválidas ou sem permissão na Tesouraria.");
    } finally {
      setLoginLoading(false);
    }
  };

  // Função para Trancar o Cofre
  const handleLogout = () => {
    localStorage.removeItem('club_token');
    setIsAuthenticated(false);
    setMembers([]);
    setDues([]);
    toast.info("Sessão da Tesouraria encerrada com segurança.");
  };

  const fetchData = async () => {
    if (!isAuthenticated) return; // Trava de segurança
    
    try {
      setLoading(true);
      // Busca os irmãos do clube
      const membersRes = await apiClube.members.getAll();
      setMembers(membersRes.data || []);

      // Busca as mensalidades do ano selecionado
      const duesRes = await apiClube.dues.getAll(selectedYear);
      setDues(duesRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Sua sessão expirou. Faça login novamente.");
      } else {
        toast.error("Erro ao carregar dados. O backend pode estar vazio ainda.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, isAuthenticated]);

  // Função provisória de UI para testar o clique no pagamento
  const handleTogglePayment = (memberId, memberName, mesIndex) => {
    const mesNome = meses[mesIndex];
    if (window.confirm(`Confirmar pagamento de R$ 170,00 para ${memberName} referente a ${mesNome}/${selectedYear}?`)) {
      toast.success(`Pagamento simulado para ${memberName} em ${mesNome}. A lógica de banco será ligada em breve.`);
      // Aqui entrará a chamada da API: apiClube.dues.create(...)
    }
  };

  // Verifica se um mês específico está pago para um membro
  const isPaid = (memberId, monthIndex) => {
    // monthIndex de 0 a 11, backend costuma usar 1 a 12
    return dues.some(d => d.memberId === memberId && d.month === monthIndex + 1 && d.status === 'pago');
  };

  // Se não estiver logado na Tesouraria, renderiza o Modal de Acesso Restrito
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] p-4">
        <Card className="w-full max-w-md border-2 border-primary/20 shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-primary/10 rounded-full">
                <Lock className="w-10 h-10 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Acesso Restrito</CardTitle>
            <p className="text-sm text-muted-foreground">
              A Tesouraria do Cabrones MC possui banco de dados isolado. Insira suas credenciais para gerar a chave de acesso.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">E-mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                />
              </div>
              <Button type="submit" className="w-full font-bold" disabled={loginLoading}>
                {loginLoading ? 'Autenticando...' : 'Desbloquear Cofre'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- CÓDIGO DA TELA PRINCIPAL (SÓ RODA SE ESTIVER LOGADO) ---
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Controle de Mensalidades</h1>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-secondary/50 px-3 py-1.5 rounded-md border border-border">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="bg-transparent border-none outline-none font-medium text-sm cursor-pointer"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <Button className="bg-primary text-primary-foreground">
            <UserPlus className="mr-2 h-4 w-4" /> Novo Irmão
          </Button>
          
          {/* Botão de Logout adicionado aqui também */}
          <div className="w-px h-8 bg-border mx-1"></div>
          <Button variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Sair
          </Button>
        </div>
      </div>

      <Card className="border-2 border-border bg-card">
        <CardHeader>
          <CardTitle>Painel Geral - {selectedYear}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px] sticky left-0 bg-card z-10 border-r border-border">
                  Irmão / Membro
                </TableHead>
                {meses.map((mes) => (
                  <TableHead key={mes} className="text-center w-[60px]">{mes}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={13} className="text-center py-10">Carregando cofre...</TableCell></TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-10 text-muted-foreground">
                    Nenhum membro cadastrado. Cadastre os irmãos para visualizar a matriz.
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium sticky left-0 bg-card z-10 border-r border-border">
                      {member.name}
                    </TableCell>
                    {meses.map((mes, index) => {
                      const pago = isPaid(member.id, index);
                      return (
                        <TableCell key={index} className="text-center p-1">
                          <button
                            onClick={() => handleTogglePayment(member.id, member.name, index)}
                            className={`w-8 h-8 rounded-md flex items-center justify-center mx-auto transition-colors ${
                              pago 
                                ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20' 
                                : 'bg-secondary text-muted-foreground hover:bg-primary/20 hover:text-primary'
                            }`}
                            title={pago ? 'Pago' : 'Pendente'}
                          >
                            {pago ? <Check className="h-4 w-4" /> : <X className="h-4 w-4 opacity-50" />}
                          </button>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Mensalidades;
